"use client";

import { useState } from "react";
import { motion } from "framer-motion";
import toast from "react-hot-toast";
import { useCart } from "@/contexts/CartContext";
import AppImage from "@/components/ui/AppImage";
import Icon from "@/components/ui/AppIcon";

export interface Product {
  id: string;
  title: string;
  subtitle: string;
  category: string;
  price: number;
  originalPrice: number;
  rating: number;
  reviews: number;
  tag?: string;
  tagColor?: string;
  ageRange: string;
  image: string;
  alt: string;
  inStock: boolean;
  isNew?: boolean;
  isBestseller?: boolean;
  purchaseLink?: string;
}

interface ProductGridProps {
  products: Product[];
}

export default function ProductGrid({ products }: ProductGridProps) {
  const [wishlist, setWishlist] = useState<Set<string>>(new Set());
  const [bouncingItems, setBouncingItems] = useState<Set<string>>(new Set());
  const { addToCart } = useCart();

  const toggleWishlist = (id: string) => {
    setWishlist((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const handleAddToCart = (product: Product, e: React.MouseEvent) => {
    e.preventDefault();
    
    if (!product.inStock) return;

    // If product has external purchase link, redirect to it
    if (product.purchaseLink) {
      window.open(product.purchaseLink, '_blank', 'noopener,noreferrer');
      toast.success(
        <div className="flex items-center gap-2">
          <span className="text-lg">🛒</span>
          <div>
            <p className="font-bold text-sm">{product.title}</p>
            <p className="text-xs text-plum/60">Opening Amazon...</p>
          </div>
        </div>,
        {
          duration: 2000,
          icon: null,
        }
      );
      return;
    }

    // Add bounce animation
    setBouncingItems((prev) => new Set(prev).add(product.id));
    setTimeout(() => {
      setBouncingItems((prev) => {
        const next = new Set(prev);
        next.delete(product.id);
        return next;
      });
    }, 600);

    // Add to cart
    addToCart(product);

    // Show success toast
    toast.success(
      <div className="flex items-center gap-2">
        <span className="text-lg">✨</span>
        <div>
          <p className="font-bold text-sm">{product.title}</p>
          <p className="text-xs text-plum/60">Added to cart!</p>
        </div>
      </div>,
      {
        duration: 2000,
        icon: null,
      }
    );
  };

  if (products.length === 0) {
    return (
      <div className="text-center py-24">
        <div className="text-6xl mb-4">📚</div>
        <h3 className="font-fraunces text-2xl font-bold text-plum mb-2">No books found</h3>
        <p className="text-plum/55 text-sm">Try adjusting your search or filters.</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5 md:gap-6">
      {products.map((product, index) => (
        <motion.div
          key={product.id}
          animate={bouncingItems.has(product.id) ? {
            scale: [1, 1.05, 0.95, 1.02, 1],
            rotate: [0, -2, 2, -1, 0],
          } : {}}
          transition={{ duration: 0.6, ease: "easeInOut" }}
          className="product-card bg-white rounded-3xl shadow-dreamy overflow-hidden group transition-all duration-300 hover:-translate-y-2 hover:shadow-2xl"
        >
          {/* Image area */}
          <div className="relative h-56 sm:h-52 md:h-64 overflow-hidden bg-plum-50">
            <AppImage
              src={product.image}
              alt={product.alt}
              fill
              priority={index < 6}
              sizes="(max-width: 640px) 100vw, (max-width: 768px) 50vw, (max-width: 1024px) 33vw, 25vw"
              className="object-cover product-img"
            />

            {/* Badges */}
            <div className="absolute top-3 left-3 flex flex-col gap-1.5">
              {product.isBestseller && (
                <span className="shimmer-badge text-plum text-xs font-black px-2.5 py-1 rounded-full">
                  ⭐ BESTSELLER
                </span>
              )}
              {product.isNew && (
                <span className="bg-plum-light text-white text-xs font-black px-2.5 py-1 rounded-full">
                  NEW
                </span>
              )}
              {product.tag && !product.isBestseller && !product.isNew && (
                <span
                  className="text-plum text-xs font-black px-2.5 py-1 rounded-full"
                  style={{ background: product.tagColor || "#C8B6E2" }}
                >
                  {product.tag}
                </span>
              )}
            </div>

            {/* Age badge */}
            <div className="absolute top-3 right-3 age-badge text-xs sm:text-sm">{product.ageRange}</div>

            {/* Wishlist */}
            <button
              onClick={() => toggleWishlist(product.id)}
              className={`absolute bottom-3 right-3 w-11 h-11 sm:w-10 sm:h-10 rounded-full flex items-center justify-center transition-all duration-300 shadow active:scale-90 ${
                wishlist.has(product.id)
                  ? "bg-rose text-white scale-110" :"bg-white/80 backdrop-blur text-rose hover:bg-white hover:scale-110"
              }`}
              aria-label={wishlist.has(product.id) ? `Remove ${product.title} from wishlist` : `Add ${product.title} to wishlist`}
            >
              <Icon name="HeartIcon" size={18} variant={wishlist.has(product.id) ? "solid" : "outline"} />
            </button>

            {/* Out of stock overlay */}
            {!product.inStock && (
              <div className="absolute inset-0 bg-white/60 backdrop-blur-sm flex items-center justify-center">
                <span className="bg-plum text-white text-xs font-bold px-3 py-1.5 rounded-full">
                  Out of Stock
                </span>
              </div>
            )}
          </div>

          {/* Info */}
          <div className="p-4 sm:p-5">
            <h3 className="font-fraunces font-bold text-plum text-base sm:text-lg leading-tight mb-1 line-clamp-1">
              {product.title}
            </h3>
            <p className="text-xs sm:text-sm text-plum/55 mb-2 line-clamp-1">{product.subtitle}</p>

            {/* Stars */}
            <div className="flex items-center gap-1 mb-3">
              <div className="stars text-sm sm:text-base">
                {"★".repeat(Math.floor(product.rating))}
                {product.rating % 1 >= 0.5 ? "½" : ""}
              </div>
              <span className="text-xs sm:text-sm font-bold text-plum">{product.rating}</span>
              <span className="text-xs text-plum/40">({product.reviews.toLocaleString()})</span>
            </div>

            {/* Price + CTA */}
            <div className="flex items-center justify-between gap-2">
              <div>
                <span className="font-black text-plum text-lg sm:text-xl">${product.price.toFixed(2)}</span>
                {product.originalPrice > product.price && (
                  <span className="text-xs text-plum/35 line-through ml-1.5">
                    ${product.originalPrice.toFixed(2)}
                  </span>
                )}
              </div>
              <button
                onClick={(e) => handleAddToCart(product, e)}
                disabled={!product.inStock}
                className={`text-xs sm:text-sm px-4 py-3 rounded-full font-bold flex items-center gap-1.5 transition-all duration-300 active:scale-95 min-h-[44px] ${
                  product.inStock
                    ? "btn-primary hover:scale-105" :"bg-gray-200 text-gray-400 cursor-not-allowed"
                }`}
                aria-label={`Add ${product.title} to cart`}
              >
                <Icon name="ShoppingCartIcon" size={14} variant="solid" />
                {product.purchaseLink ? "Buy on Amazon" : "Add"}
              </button>
            </div>
          </div>
        </motion.div>
      ))}
    </div>
  );
}